import { createConnect } from './connect';
import { Event } from './event';
import { env } from './env';
import { assert } from './utils';

interface Config {
    // 应用ID， 在控制台应用详情查看
    appId: string;

    container?: string;

    onCloseModal?(): Promise<boolean | void> | void | boolean;
    /**
     *  获取使用权益凭证
     */
    getUseRightCert?(info: {
        appId: string;
        useKey: string;
        abilityCode: string;
        workId: string;
    }): Promise<string | false | Error>;
    /**
     * 获取用户授权标识
     */
    authorize?(): Promise<string>;
}

type FileType = 'jpg' | 'png' | 'gif' | 'ppt' | 'pdf' | 'mp4';
type EditorModuleType =
    | 'panel.template'
    | 'panel.element'
    | 'panel.text'
    | 'panel.photo'
    | 'panel.widget'
    | 'panel.chart'
    | 'panel.component'
    | 'panel.my-resource';

interface Canva {
    addImage(url: string, options?: { width: number; height: number }): Promise<void>;
}

export function createTemplateEditor(config: Config) {
    const event = new Event();

    let canva: Canva | null = null;
    let load = false;
    const tempCallbacks: Function[] = [];
    function cleanCallBack() {
        tempCallbacks.forEach((fn) => fn());
        tempCallbacks.length = 0;
    }

    const iframeManager = createConnect<{
        init(data: any): void;
        addImage(url: string, options?: { width: number; height: number }): Promise<void>;
    }>({
        appId: config.appId,
        container: config.container,
        async onClose() {
            if (config.onCloseModal) {
                const res = await config.onCloseModal();
                if (res === false) return false;
            }
            event.emit('close');
        },
        methods: {
            async emit(name: string, ...args: any) {
                event.emit(name, ...args);
            },
            async onload() {
                event.emit('load');
            },
            async onSelectTemplate(info: any) {
                event.emit('select.template', info);
                return false;
            },
            async onTemplateLoad() {
                canva = await createCanva();
                event.emit('template.load');
            },
            async getUseRightToken(info: any) {
                if (!config.getUseRightCert) return false;
                const token = await config.getUseRightCert(info);
                if (token instanceof Error) {
                    return {
                        message: token.message,
                    };
                }
                return token;
            },
            async getUserCode() {
                if (!config.authorize) return false;
                try {
                    return await config.authorize();
                } catch (error) {
                    close();
                }
            },
        },
    });

    async function createCanva() {
        const servcie = await iframeManager.getService();
        return {
            async addImage(url: string, options?: { width: number; height: number }) {
                servcie.addImage(url, options);
            },
        };
    }

    function createDesign(
        options: {
            templateId?: string | number;
            size?: { width: number; height: number };
            categoryId?: string | number;
            fileTypes?: FileType[];
            okText?: string;
            disableModules?: EditorModuleType[];
        } = {},
    ) {
        if (options.categoryId) {
            assert(
                ['number', 'string'].includes(typeof options.categoryId),
                'categoryId 参数类型有误',
            );
        }
        cleanCallBack();
        load = false;
        const params: Record<string, any> = {
            version: config.authorize ? 'TE002' : 'TE001',
            buttonText: options.okText || '保存',
        };
        if (options.templateId) {
            params.id = options.templateId;
        } else {
            params.mode = 'create';
            if (options.size) {
                params.width = options.size.width;
                params.height = options.size.height;
            }
        }
        if (options.categoryId) {
            params.filterId = options.categoryId;
        }
        iframeManager.update(env.TEMPLATE_EDITOR_URL, params);

        event.once('load', async () => {
            load = true;
            const e = await iframeManager.getService();
            await e.init({
                appId: config.appId,
                version: config.authorize ? 'TE002' : 'TE001',
                fileTypes: options.fileTypes,
                disableModules: options.disableModules,
                v: env.VERSION,
                // @ts-ignore ignore
                filterPaidMaterial: options.filterPaidMaterial,
                categoryId: options.categoryId,
            });
            event.emit('editor.load');
        });

        iframeManager.insert();
        return new Promise<{ files: Blob[]; workId: number; title: string; type: string } | false>(
            (resolve) => {
                tempCallbacks.push(event.once('save', (v) => resolve(v)));
                tempCallbacks.push(event.once('close', () => resolve(false)));
            },
        );
    }

    function editDesign(options: {
        id: string | number;
        fileTypes?: FileType[];
        categories?: number[];
        okText?: string;
        disableModules?: EditorModuleType[];
        filterPaidMaterial?: never;
    }) {
        if (options.categories) {
            assert(Array.isArray(options.categories), 'categories 参数必须是 number 数组类型');
            assert(
                options.categories.map((c) => +c).filter(Boolean).length ===
                    options.categories.length,
                'categories 参数必须是 number 数组类型',
            );
        }
        cleanCallBack();
        load = false;
        iframeManager.update(env.TEMPLATE_EDITOR_URL, {
            id: options.id,
            buttonText: options.okText || '保存',
            mode: 'user',
            version: config.authorize ? 'TE002' : 'TE001',
        });

        event.once('load', async () => {
            load = true;
            const e = await iframeManager.getService();

            await e.init({
                appId: config.appId,
                version: config.authorize ? 'TE002' : 'TE001',
                disableModules: options.disableModules,
                categoryId: options.categories?.join(','),
                v: env.VERSION,
                // @ts-ignore ignore
                filterPaidMaterial: options.filterPaidMaterial,
                fileTypes: options.fileTypes,
            });
            event.emit('editor.load');
        });

        iframeManager.insert();
        return new Promise<{ files: Blob[]; workId: number; title: string; type: string } | false>(
            (resolve) => {
                tempCallbacks.push(event.once('save', (v) => resolve(v)));
                tempCallbacks.push(event.once('close', () => resolve(false)));
            },
        );
    }

    function openTemplateList(options: {
        categories?: number[] | string[];
        onSelectTemplate?(info: { id: string | number; categoryId: string | number }): void;
    }) {
        if (options.categories) {
            assert(Array.isArray(options.categories), 'categories 参数必须是 number 数组类型');
            assert(
                options.categories.map((c) => +c).filter(Boolean).length ===
                    options.categories.length,
                'categories 参数必须是 number 数组类型',
            );
        }
        load = false;
        cleanCallBack();
        iframeManager.update(env.OPEN_PLATFORM_ORIGIN + '/template/search', {
            app_id: config.appId,
            version: config.authorize ? 'TE002' : 'TE001',
        });
        event.once('load', async () => {
            load = true;
            const e = await iframeManager.getService();
            e.init({
                appId: config.appId,
                v: env.VERSION,
                version: config.authorize ? 'TE002' : 'TE001',
                categories: options.categories,
            });
        });
        if (options.onSelectTemplate) {
            tempCallbacks.push(event.add('select.template', options.onSelectTemplate));
        }
        iframeManager.insert();
        return new Promise<{ id: string | number; categoryId: string | number } | false>(
            (resolve) => {
                tempCallbacks.push(event.once('select.template', (v) => resolve(v)));
                tempCallbacks.push(event.once('close', () => resolve(false)));
            },
        );
    }

    function close() {
        event.emit('close');
        iframeManager.remove();
    }

    function onSave(
        fn: (info: { workId: string; files: Blob[]; title: string; type: string }) => void,
    ) {
        event.add('save', fn);
    }

    function onClose(fn: () => any) {
        event.add('close', fn);
    }

    function onLoad(fn: () => any) {
        event.add('editor.load', fn);
    }

    function onTemplateLoad(fn: () => any) {
        event.add('template.load', fn);
    }

    return {
        get canva() {
            return canva;
        },
        get isLoad() {
            return load;
        },
        openTemplateList,
        close,
        onSave,
        onTemplateLoad,
        onClose,
        onLoad,
        editDesign,
        createDesign,
    };
}
